/**
 * #4625 — ask the operating system to show its own "rate this app" prompt.
 *
 * A thin wrapper around the two local native plugins (`CsReviewPromptPlugin`,
 * one Swift and one Java). It is the ONLY way the app asks for a rating, and it
 * shows nothing of its own: the wording, the stars and the buttons all belong to
 * Apple and Google. Showing a "do you like the app?" question and sending only
 * the happy answers to the store is called review gating — Google bans it and
 * since 2025 punishes it by deleting *all* of an app's reviews, and Apple treats
 * it as review manipulation. See design-docs/review-prompts.md.
 *
 * **Neither store tells us anything back.** No callback, no rating, not even
 * whether the prompt appeared. `requestAppReviewAsync` resolving means only that
 * the request was made — never that anybody saw anything, and never that anybody
 * rated us. Do not build anything that assumes otherwise.
 *
 * The one measurement we get is that the caller decided to ask, which is the
 * caller's own to record (the cap marker, and the `review_prompt_attempted`
 * event in a later step).
 */
import { Capacitor, registerPlugin } from '@capacitor/core';
import { LoggingService } from 'services/logging.service';
import { errorReportFields } from 'services/error-patterns';

interface CsReviewPromptPluginInterface {
  /** Ask the store to show its review prompt. Resolves once the request was made. */
  requestReview(): Promise<void>;
}

const CsReviewPromptPlugin = registerPlugin<CsReviewPromptPluginInterface>('CsReviewPromptPlugin');

/**
 * True when this build can show a store review prompt at all.
 *
 * Exported so a caller can skip the surrounding work — reading the gate's
 * inputs, writing the cap marker — rather than doing all of it and then finding
 * there was nothing to show.
 */
export function canShowStoreReviewPrompt(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Ask the store to show its review prompt.
 *
 * **On the web this does nothing at all, and that is not a failure.** A browser
 * has no store review prompt, so there is nothing to call and nothing has gone
 * wrong. Reporting it would fill telemetry with records of the app behaving
 * correctly, which is how a real report becomes unfindable — and every local
 * development session and every E2E run would produce one.
 *
 * A genuine native failure IS reported, so the two stay distinguishable. In
 * practice only Android can produce one: it rejects when Play could not prepare
 * a review flow at all. iOS never reports anything, by design.
 *
 * Never throws. The caller is a "this looks like a good moment" line at the end
 * of a user action, and failing to ask for a rating must not be able to break
 * whatever the person was actually doing.
 *
 * **Call it and forget it — do not `await` this before doing something else.**
 * It never throws, but on Android it can in principle never settle either: the
 * native side resolves only from a Play completion listener, so if the activity
 * is torn down while the flow is in progress that listener may never fire, and
 * there is no timeout here. Nothing waits on a review prompt, so a bounded wait
 * would add a moving part for no gain — but a caller that awaited it before its
 * next step could stall that step, which is why this says so rather than leaving
 * it to be rediscovered.
 */
export async function requestAppReviewAsync(logging: LoggingService): Promise<void> {
  if (!canShowStoreReviewPrompt()) return;

  try {
    await CsReviewPromptPlugin.requestReview();
  } catch (err) {
    // Reaching here means the request itself failed, NOT that the person
    // declined or that a store quota suppressed the prompt — neither of those is
    // visible to us on either platform. So this really is worth a look, even
    // though nothing is broken for the user: they simply were not asked.
    logging.trackSilentProblem('ReviewPromptRequestFailed', {
      platform: Capacitor.getPlatform(),
      ...errorReportFields(err),
    });
  }
}
